// Persistence: IndexedDB for records, localStorage for scalars.
//
// The `records` store is generic on purpose — one object store with an id, a
// payload, and bookkeeping fields. Whatever your app saves goes in `data`.
// `dirty` and `updatedAt` exist so the sync addon can bolt on later without
// migrating anything.
//
// IN DEMO MODE THERE IS NO PERSISTENCE AT ALL. Both stores below are swapped
// for memory, and that is the whole of `/demo` keeping its promise — see
// `demoStorage` for why it is here rather than at each of the thirty call
// sites that write.

import { isDemo } from './demo.js';

const DB_NAME = 'app';
const DB_VERSION = 1;
const STORE = 'records';

let dbPromise;

function open() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' });
        os.createIndex('updatedAt', 'updatedAt');
        os.createIndex('dirty', 'dirty');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx(mode, fn) {
  return open().then(
    (db) =>
      new Promise((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const store = t.objectStore(STORE);
        let result;
        try { result = fn(store); } catch (e) { reject(e); return; }
        t.oncomplete = () => resolve(result?.result ?? result);
        t.onerror = () => reject(t.error);
      }),
  );
}

const diskRecords = {
  put(record) {
    const now = Date.now();
    return tx('readwrite', (s) =>
      s.put({ dirty: 1, createdAt: now, ...record, updatedAt: now }),
    );
  },
  get(id) { return tx('readonly', (s) => s.get(id)); },
  all() { return tx('readonly', (s) => s.getAll()); },
  delete(id) { return tx('readwrite', (s) => s.delete(id)); },
  count() { return tx('readonly', (s) => s.count()); },
  /** Records the sync addon has not yet pushed. */
  dirty() { return tx('readonly', (s) => s.index('dirty').getAll(1)); },
};

// --- demo mode: the same two stores, in memory ------------------------------
//
// WHY THIS IS HERE AND NOT AT EVERY WRITE. `/demo` promises that nothing it
// shows is saved and nothing it does reaches the real app. It now reads LIVE
// data, which means every ordinary code path in the app runs there: a live
// disruption is added to the ledger, a tapped station is saved, a board is
// cached, a planned trip is added to the recents. Each of those writes is
// somebody's correct code, and asking thirty call sites to remember demo mode
// is asking for the one that forgets — whose symptom is not a crash but a
// stranger's sample rows turning up in a real install.
//
// So the promise is kept once, at the bottom, where every write already has
// to pass. The demo gets an EMPTY store, not a filtered view of the real one:
// reading the real rows would show a developer's own saved stations inside
// what is meant to be a clean first-run, and `/demo` and `/app` share an
// origin in development.
//
// Gone on reload is the correct behaviour, and it is the same rule the run
// patterns and the fares already follow one directory up.

function memoryRecords() {
  const rows = new Map();
  return {
    put(record) {
      const now = Date.now();
      rows.set(record.id, { dirty: 1, createdAt: now, ...record, updatedAt: now });
      return Promise.resolve(record.id);
    },
    get(id) { return Promise.resolve(rows.get(id)); },
    all() { return Promise.resolve([...rows.values()]); },
    delete(id) { rows.delete(id); return Promise.resolve(); },
    count() { return Promise.resolve(rows.size); },
    dirty() { return Promise.resolve([...rows.values()].filter((r) => r.dirty === 1)); },
  };
}

export const records = isDemo.value ? memoryRecords() : diskRecords;

// --- preferences ------------------------------------------------------------
// A Proxy over localStorage so callers read and write plain properties.
// Values are JSON, so booleans survive the round trip as booleans.
//
// In demo mode the backing store is a Map — same reasoning as `records` above,
// and the same two halves to it: a demo write never lands, and a demo read
// never sees the real app's quiet hours, rules, cached boards or saved
// destinations. The demo is seeded explicitly; anything it did not seed, it
// genuinely does not have.

const PREFIX = 'pref:';

// JSON in and JSON out, exactly as localStorage does it. A Map holding the
// live object would hand a caller back the array it wrote, and a reader that
// mutated what it read would be editing the store through a reference no
// deployed build has — a demo-only bug, which is the worst kind to have.
const demoPrefs = new Map();

export const prefs = isDemo.value ? new Proxy({}, {
  get(_, key) {
    const raw = demoPrefs.get(String(key));
    return raw === undefined ? undefined : JSON.parse(raw);
  },
  set(_, key, value) {
    demoPrefs.set(String(key), JSON.stringify(value));
    return true;
  },
  deleteProperty(_, key) {
    demoPrefs.delete(String(key));
    return true;
  },
}) : new Proxy(
  {},
  {
    get(_, key) {
      try {
        const raw = localStorage.getItem(PREFIX + String(key));
        return raw === null ? undefined : JSON.parse(raw);
      } catch { return undefined; }
    },
    set(_, key, value) {
      try { localStorage.setItem(PREFIX + String(key), JSON.stringify(value)); } catch { /* private mode */ }
      return true;
    },
    deleteProperty(_, key) {
      try { localStorage.removeItem(PREFIX + String(key)); } catch { /* ignore */ }
      return true;
    },
  },
);

/** Apply saved preferences at boot, filling defaults for anything unset. */
export function hydrateFlags(defaults = { sound: true, haptics: true }) {
  for (const [k, v] of Object.entries(defaults)) {
    if (prefs[k] === undefined) prefs[k] = v;
  }
  return { ...defaults, ...Object.fromEntries(Object.keys(defaults).map((k) => [k, prefs[k]])) };
}
